import { extendTheme } from "@chakra-ui/react";
import { createBreakpoints } from "@chakra-ui/theme-tools";
// import { onHoverDropShadow } from "../_components/styled/variable";
import {
  FaRegSurprise,
  FaRegSmile,
  FaInfo,
  FaExclamationTriangle,
} from "react-icons/fa";

const colors = {
  brand: {
    "grey-darker": "#323232",
    primary: "#FFAC41",
    dark: "#00000029",
    darker: "#00000052",
    negative: "#C84B4B",
    positive: "#6DD300",
    positive_light: "#85DE55",
    positive_darker: "#46b900",
    safe_light: "#68D5FF",
    safe_dark: "#008CD3",
    white: "#ffffff",
    dark_00: "#1E1E1E",
    dark_02: "#232323",
    dark_04: "#323232",
    dark_05: "#282828",
    glass_light_10: "rgba(255, 255, 255, 0.08)",
    glass_dark_08: "rgba(0, 0, 0, 0.16)",
    glass_dark_10: "rgba(0, 0, 0, 0.08)",
    success: "#85DE55",
    "border-color": "#494949",
    warning: "#DB7338",
    candy: "#FF4A98",
    cream: "#FFFBBD",
    overlayDark: "#00000080",
    danger:"#dc3545",
    shamrock_green:"#109d59",
    rusty_red: "#da2c4c",
    violet_blue:"#7b50ff",
    blue_cola: "#008cd3"
  },
  gray: {
    100: "#d8d8d8",
    200: "#939393",
    300: "#888888",
    400: "#3e4144",
    500: "#2b2b2b",
    600: "#1e1e1e",
    700: "#1a1a1a",
    800: "#151515",
    900: "#131313",
    1000: "#FFFFFF14",
    1100: "#e2e2e2",
    1200: "#00000014",
    1300: "#858585",
  },
  primary: {
    100: "#FFE086",
    200: "#FFAC41",
    300: "#ffd350",
    400: "#FFAD00",
  },
  whiteAlpha: {
    100: "rgba(255, 255, 255, 0.08)",
    200: "rgba(255, 255, 255, 0.16)",
    300: "#93939329",
  },
};

const breakpoints = createBreakpoints({
  xs: "0px",
  sm: "576px",
  md: "768px",
  lg: "960px",
  xl: "1200px",
  "2xl": "1536px",
});

const shadows = {
  md: "0px 0px 16px rgba(0, 0, 0, 0.32)",
};

const components = {
  Alert: {
    baseStyle: {
      container: {
        padding: "2",
        fontSize: "sm",
      },
      description: {
        a: {
          color: "inherit",
          textDecoration: "underline",
          _hover: {
            textDecoration: "none",
          },
        },
      }
    },
  },
  Checkbox: {
    defaultProps: {
      colorScheme: "gray",
    },
  },
  Button: {
    defaultProps: {
      size: "md",
      variant: "secondary",
    },
    baseStyle: {
      fontWeight: "500",
      color: "gray.100",
      borderRadius: "8px",
    },
    variants: {
      solid: {
        background: "primary.400",
        color: "gray.600",
        _hover: {
          background: "primary.300",
          textDecoration: "none",
          _disabled: {
            background: "primary.400",
            color: "gray.600",
          },
        },
        _active: {
          background: "primary.100",
        },
        _focus: {
          background: "primary.100",
        },
        _disabled: {
          background: "whiteAlpha.100",
          color: "gray.300",
        },
        _loading: {
          background: "primary.400",
          color: "gray.600",
        },
      },
      danger: {
        background: "brand.negative",
        color: "white",
        _hover: {
          background: "red.500",
          textDecoration: "none",
          _disabled: {
            background: "brand.negative",
            color: "white",
          },
        },
        _active: {
          background: "red.400",
        },
        _focus: {
          background: "red.400",
        },
        _disabled: {
          background: "whiteAlpha.100",
          color: "gray.300",
        },
        _loading: {
          background: "brand.negative",
          color: "white",
        },
      },
      secondary: {
        background: "whiteAlpha.100",
        color: "gray.100",
        _hover: {
          background: "whiteAlpha.200",
          color: "white",
        },
        _loading: {
          background: "whiteAlpha.100",
          color: "primary.400",
        },
        _focus: {
          background: "whiteAlpha.100",
          color: "white",
        },
        _active: {
          background: "whiteAlpha.100",
          color: "primary.400",
        },
        _disabled: {
          background: "whiteAlpha.100",
          color: "gray.300",
        },
      },
      ghost: {
        background: "transparent",
        color: "gray.100",
        _hover: {
          background: "whiteAlpha.200",
          color: "white",
        },
        _focus: {
          background: "whiteAlpha.100",
          color: "primary.400",
        },
        _active: {
          background: "whiteAlpha.100",
          color: "primary.400",
        },
        _disabled: {
          background: "transparent",
          color: "gray.300",
        },
        _loading: {
          background: "transparent",
          color: "gray.100",
        },
      },
      unstyled: {
        background: "transparent",
        color: "gray.100",
        _hover: {
          background: "transparent",
          color: "brand.primary",
        },
        _focus: {
          background: "transparent",
          color: "gray.100",
          boxShadow: "none",
        },
        _active: {
          background: "transparent",
          color: "primary.400",
        },
        _disabled: {
          background: "transparent",
          color: "gray.300",
        },
        _loading: {
          background: "transparent",
          color: "gray.100",
        },
      },
      outline: {
        borderColor: "primary.400",
        color: "primary.400",
        background: "transparent",
        _hover: {
          background: "primary.400",
          color: "gray.600",
        },
        _focus: {
          background: "primary.400",
          color: "gray.600",
        },
        _active: {
          background: "primary.400",
          color: "gray.600",
        },
        _loading: {
          borderColor: "primary.400",
          color: "primary.400",
          background: "transparent",
        },
      },
      heading: {
        bg: "transparent",
        px: "0",
        fontWeight: "400",
        textTransform: "uppercase",
        _active: {
          bg: "transparent"
        },
        _focus: {
          bg: "transparent"
        },
        _hover: {
          bg: "transparent",
          color: "primary.400"
        }
      }
    },
  },
  CloseButton: {
    baseStyle: {
      border: "0",
      background: "transparent",
      color: "gray.200",
    },
    defaultProps: {
      size: "sm",
    },
  },
  IconButton: {
    baseStyle: {
      border: "0",
      background: "transparent",
      color: "brand.white",
    },
    defaultProps: {
      size: "md",
      background: "transparent",
      color: "brand.white",
    },
    solid: {
      background: "transparent",
      color: "brand.white",
      _active: {
        background: "transparent",
        color: "primary.400",
      },
    },
  },
  Drawer: {
    parts: ["body", "header", "footer"],
    baseStyle: {
      header: {
        backgroundColor: "gray.500",
        color: "gray.100",
        paddingInlineEnd: 3,
        paddingInlineStart: 3,
      },
      body: {
        backgroundColor: "gray.500",
        color: "gray.100",
        paddingInlineEnd: 3,
        paddingInlineStart: 3,
      },
      footer: {
        backgroundColor: "gray.500",
        paddingInlineEnd: 3,
        paddingInlineStart: 3,
      },
    },
  },
  Heading: {
    baseStyle: {
      color: "gray.100",
    },
  },
  Modal: {
    parts: ["dialogContainer", "dialog", "header"],
    baseStyle: ({ ...props }) => ({
      dialogContainer: {
        overflow: props.scrollBehavior === "inside" ? "initial" : "auto", // fix inside scroll modal issue
      },
      dialog: {
        background: "brand.dark_02",
        color: "white",
      },
      header: {
        fontSize: "md",
        fontWeight: "500",
        color: "brand.white",
        paddingY: "3",
      },
    }),
  },
  Popover: {
    parts: ["content", "header", "body", "arrow"],
    baseStyle: {
      arrow: {
        border: "0",
      },
      content: {
        "--popper-arrow-bg": "var(--chakra-colors-gray-500)",
        background: "gray.500",
        color: "white",
        padding: "0",
        border: "0",
      },
      header: {
        padding: "4",
        borderBottomColor: "gray.400",
        borderBottomStyle: "solid",
      },
      body: {
        padding: "4",
        // boxShadow: `0px 0px 40px ${onHoverDropShadow}`,
        borderRadius: "var(--chakra-radii-md)",
      },
    },
  },
  List: {
    parts: ["container", "item"],
    baseStyle: {
      container: {
        margin: "0",
        padding: "0",
      },
      item: {
        margin: "0",
        padding: "0",
      },
    },
  },
  Menu: {
    parts: ["list", "item"],
    baseStyle: {
      list: {
        border: "0",
        background: "gray.500",
        color: "white",
        "&:focus:not([data-focus-visible-added])": {
          shadow: "dark-lg", // default shadow
        },
      },
      item: {
        background: "transparent",
        border: "0",
        color: "white",
        _hover: {
          color: "primary.200",
        },
      },
    },
  },
  Tabs: {
    parts: ["tab", "tablist", "tabpanels", "tabpanel"],
    baseStyle: {
      tab: {
        background: "transparent",
        border: "0",
        color: "white",
      },
      tabpanel: {
        paddingX: "0",
      },
    },
    variants: {
      secondary: {
        tab: {
          fontWeight: 500,
          paddingLeft: "0.6rem",
          paddingRight: "0.6rem",
          paddingTop: "0.2rem",
          paddingBottom: "0.2rem",
          borderRadius: "3rem",
          borderWidth: "2px",
          borderStyle: "solid",
          borderColor: "transparent",
          _selected: {
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: "primary.400",
          },
        },
      },
      unstyled: {
        tabpanel: {
          padding: "0",
        },
      },
      line: {
        tab: {
          color: "gray.100",
          borderBottom: "2px solid",
          borderColor: "transparent",
          marginBottom: "-1px",
          fontWeight: 500,
          _hover: {
            backgroundColor: "transparent",
            color: "brand.white",
            borderColor: "whiteAlpha.200",
          },
          "&[aria-selected=true]:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[data-selected]:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[aria-selected=true]:hover": {
            backgroundColor: "transparent",
            borderColor: "primary.400",
            color: "primary.400",
          },
          "&[data-selected]:hover": {
            backgroundColor: "transparent",
            borderColor: "primary.400",
            color: "primary.400",
          },
          "&[aria-selected=true]:hover:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[data-selected]:hover:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          _selected: {
            borderColor: "primary.400",
            color: "primary.400",
          },
          _disabled: {
            borderColor: "whiteAlpha.100",
            color: "gray.300",
          },
          _focus: {
            borderColor: "primary.400",
            color: "primary.400",
          },
        },
        tablist: {
          borderBottom: "1px solid",
          borderColor: "whiteAlpha.100",
        },
        tabpanel: {
          padding: "16px 0px 0px 0px",
        },
      },
      panel: {
        root: {
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        },
        tabpanels: {
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        },
        tabpanel: {
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          padding: "16px 0px 0px 0px",
          "::-webkit-scrollbar-track": {
            backgroundColor: "transparent"
          },
          "::-webkit-scrollbar-track-piece": {
            backgroundColor: "transparent"
          }
        },
        tab: {
          color: "gray.100",
          borderBottom: "2px solid",
          borderColor: "transparent",
          marginBottom: "-1px",
          fontWeight: 500,
          _hover: {
            backgroundColor: "transparent",
            color: "brand.white",
            borderColor: "whiteAlpha.200",
          },
          "&[aria-selected=true]:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[data-selected]:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[aria-selected=true]:hover": {
            backgroundColor: "transparent",
            borderColor: "primary.400",
            color: "primary.400",
          },
          "&[data-selected]:hover": {
            backgroundColor: "transparent",
            borderColor: "primary.400",
            color: "primary.400",
          },
          "&[aria-selected=true]:hover:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          "&[data-selected]:hover:active": {
            backgroundColor: "transparent",
            borderColor: "primary.100",
            color: "primary.100",
          },
          _selected: {
            borderColor: "primary.400",
            color: "primary.400",
          },
          _disabled: {
            borderColor: "whiteAlpha.100",
            color: "gray.300",
          },
          _focus: {
            borderColor: "primary.400",
            color: "primary.400",
          },
        },
        tablist: {
          borderBottom: "1px solid",
          borderColor: "whiteAlpha.100",
        },
      },
    },
  },
  Text: {
    baseStyle: {
      margin: "0",
    },
  },
  Radio: {
    defaultProps: {
      variant: "primary",
    },
    variants: {
      primary: {
        control: {
          borderWidth: "1px",
          borderColor: "gray.300",
          backgroundColor: "brand.glass_dark_08",
          _checked: {
            borderColor: "primary.400",
            backgroundColor: "brand.glass_dark_08",
            _hover: {
              borderColor: "primary.400",
              backgroundColor: "brand.glass_dark_08",
            },
            _before: {
              width: "calc(100% - 4px)",
              height: "calc(100% - 4px)",
              color: "primary.400",
            },
          },
        },
      },
      secondary: {
        control: {
          borderWidth: "1px",
          borderColor: "brand.glass_light_10",
          backgroundColor: "brand.glass_dark_08",
          _checked: {
            borderColor: "brand.glass_light_10",
            backgroundColor: "brand.glass_dark_08",
            _hover: {
              borderColor: "brand.glass_light_10",
              backgroundColor: "brand.glass_dark_08",
            },
            _before: {
              width: "calc(100% - 2px)",
              height: "calc(100% - 2px)",
              color: "white",
            },
          },
        },
      },
      switch: {
        container: {
          margin: "0",
        },
        control: {
          display: "none",
        },
        label: {
          borderRadius: "100vh",
          paddingX: "3.5",
          paddingY: "2.5",
          margin: "0",
          lineHeight: "1.2",
          color: "gray.100",
          transition: "background-color 250ms ease-in-out, color 250ms ease-in-out",
          _checked: {
            background: "brand.glass_light_10",
            color: "primary.400",
          },
        },
      },
    },
  },
  RadioGroup: {
    variants: {
      switch: {
        display: "inline-flex",
        background: "brand.glass_dark_08",
        borderRadius: "100vh",
        padding: "1",
        "& > *:not(:first-of-type)": {
          marginLeft: "1",
        },
      },
    },
  },
  Tooltip: {
    baseStyle: {
      "--popper-arrow-bg": "var(--chakra-colors-black)",
      background: "black",
      color: "white",
      fontWeight: "normal",
      fontSize: "xs",
      padding: "0.25rem 0.5rem",
    },
    variants: {
      error: {
        "--popper-arrow-bg": "var(--chakra-colors-brand-negative)",
        background: "brand.negative",
        color: "white",
      },
    },
  },
  Select: {
    parts: ["field", "icon"],
    baseStyle: {
      icon: {
        cursor: "pointer",
      },
      field: {
        cursor: "pointer",
        "> option, > optgroup": {
          color: "gray.100",
        },
      },
    },
    variants: {
      filled: {
        icon: {
          color: "gray.100",
        },
        field: {
          color: "gray.100",
          background: "gray.900",
          border: "1px solid",
          borderColor: "whiteAlpha.100",
          borderRadius: "lg",
          _focus: {
            // borderColor: "brand.primary",
            borderColor: "transparent",
            background: "whiteAlpha.300",
          },
          _hover: {
            background: "whiteAlpha.200",
          },
          _invalid: {
            borderColor: "red.500",
          },
        },
      },
      empty: {
        icon: {
          color: "gray.300",
        },
        field: {
          color: "gray.300",
          background: "gray.900",
          borderRadius: "lg",
          _focus: {
            background: "whiteAlpha.300",
            color: "gray.100",
          },
          _hover: {
            background: "whiteAlpha.200",
            color: "gray.100",
          },
          _invalid: {
            borderColor: "red.500",
          },
        },
      },
      "filled-light": {
        icon: {
          color: "gray.900",
        },
        field: {
          color: "gray.900",
          background: "gray.100",
          border: "2px solid",
          borderColor: "transparent",
          borderRadius: "8px",
          _focus: {
            // borderColor: "brand.primary",
            borderColor: "transparent",
            background: "blackAlpha.300",
          },
          _hover: {
            background: "blackAlpha.200",
          },
          _invalid: {
            borderColor: "red.500",
          },
        },
      },
    },
    defaultProps: {
      variant: "filled",
    },
  },
  Input: {
    parts: ["addon", "field"],
    baseStyle: {
      field: {
        _autofill: {
          border: "1px solid transparent",
          textFillColor: "#d8d8d8", //it is not picking the color from theme so need to add hexa
          boxShadow: "0 0 0px 1000px transparent inset",
          transition: "background-color 5000s ease-in-out 0s",
        },
      },
    },
    variants: {
      filled: {
        addon: {
          color: "gray.100",
          // background: "brand.dark",
        },
        field: {
          color: "gray.100",
          background: "brand.dark",
          border: "1px solid",
          borderColor: "whiteAlpha.100",
          borderRadius: "8px",
          _placeholder: {
            color: "gray.300",
          },
          _focus: {
            background: "brand.darker",
            borderWidth: "1px",
            borderColor: "gray.1000",
            color: "brand.white",
          },
          _hover: {
            background: "brand.darker",
            borderColor: "gray.1000",
          },
          _invalid: {
            borderWidth: "1px",
            borderColor: "brand.negative",
          },
          _disabled: {
            background: "blackAlpha.200",
            color: "gray.300",
            borderColor: "whiteAlpha.100",
          },
        },
      },
      "filled-light": {
        addon: {
          color: "gray.900",
        },
        field: {
          color: "gray.900",
          background: "gray.100",
          border: "2px solid",
          borderColor: "transparent",
          borderRadius: "8px",
          _focus: {
            borderColor: "brand.primary",
          },
          _hover: {
            background: "blackAlpha.200",
          },
          _invalid: {
            borderColor: "red.500",
          },
        },
      },
    },
    defaultProps: {
      variant: "filled",
    },
  },
  Link: {
    baseStyle: {
      color: "primary.200",
      _hover: {
        "&:not(.chakra-button)": {
          color: "primary.300",
        },
      },
    },
  },
  
  Textarea: {
    variants: {
      filled: {
        color: "gray.100",
        background: "blackAlpha.300",
        border: "2px solid",
        borderColor: "transparent",
        borderRadius: "8px",
        _placeholder: {
          color: "gray.300",
        },
        _focus: {
          background: "blackAlpha.400",
          borderColor: "transparent",
          color: "brand.white",
        },
        _hover: {
          background: "blackAlpha.300",
        },
        _invalid: {
          borderColor: "brand.negative",
        },
        _disabled: {
          background: "blackAlpha.200",
          color: "gray.300",
        },
        "::-webkit-resizer": {
          background: "none",
        },
      },
      "filled-light": {
        color: "gray.900",
        background: "gray.100",
        border: "2px solid",
        borderColor: "transparent",
        borderRadius: "8px",
        _focus: {
          borderColor: "brand.primary",
        },
        _hover: {
          background: "blackAlpha.200",
        },
        _invalid: {
          borderColor: "red.500",
        },
        "::-webkit-resizer": {
          background: "none",
        },
      },
    },
    defaultProps: {
      variant: "filled",
    },
  },
  Table: {
    parts: ["table", "th", "td", "tr"],
    variants: {
      simple: {
        th: {
          fontWeight: "normal",
          fontSize: "sm",
          textTransform: "none",
          letterSpacing: "0",
          borderColor: "gray.400",
          color: "gray.100",
        },
        td: {
          borderColor: "gray.400",
        },
      },
      complex: {
        th: {
          px: "2",
          fontWeight: "400",
          fontSize: "sm",
          letterSpacing: "0",
          borderColor: "whiteAlpha.100",
          color: "gray.100",
          _last:{
            pr: "4"
          },
          '&[data-is-numeric]': {
            textAlign: "end",
          },
        },
        td: {
          px: "2",
          py: "2",
          verticalAlign: "top",
          fontWeight: "400",
          fontSize: "sm",
          letterSpacing: "0",
          color: "gray.100",
          _last:{
            pr: "4"
          },
          '&[data-is-numeric]': {
            textAlign: "end",
          },
        },
        tr: {
          _even: {
            td: {
              background: "blackAlpha.100"
            }
          },
          _odd: {
            td: {
              background: "blackAlpha.300",
            }
          }
        }
      },
    },
  },
  NumberInput: {
    parts: ["stepper", "field"],
    variants: {
      filled: {
        stepper: {
          color: "gray.100",
        },
        field: {
          color: "gray.100",
          background: "blackAlpha.300",
          border: "1px solid",
          borderColor: "whiteAlpha.100",
          borderRadius: "8px",
          _placeholder: {
            color: "gray.300",
          },
          _focus: {
            background: "blackAlpha.400",
            borderWidth: "2px",
            borderColor: "whiteAlpha.200",
            color: "brand.white",
          },
          _hover: {
            background: "blackAlpha.300",
            borderColor: "whiteAlpha.200",
          },
          _invalid: {
            borderWidth: "2px",
            borderColor: "brand.negative",
          },
          _disabled: {
            background: "blackAlpha.200",
            color: "gray.300",
            borderColor: "whiteAlpha.100",
          },
        },
      },
      "filled-light": {
        stepper: {
          color: "gray.900",
        },
        field: {
          color: "gray.900",
          background: "gray.100",
          border: "2px solid",
          borderColor: "transparent",
          borderRadius: "8px",
          _focus: {
            borderColor: "brand.primary",
          },
          _hover: {
            background: "blackAlpha.200",
          },
          _invalid: {
            borderColor: "red.500",
          },
        },
      },
    },
    defaultProps: {
      variant: "filled",
    },
  },
  Badge: {
    baseStyle: {
      textTransform: "capitalize",
      borderRadius: "full",
    },
    variants: {
      solid: {
        background: "primary.400",
        color: "gray.600",
        _hover: {
          textDecoration: "none",
        },
      },
    },
  },
  PinInput: {
    variants: {
      filled: {
        color: "gray.100",
        background: "gray.900",
        border: "2px solid",
        borderColor: "transparent",
        borderRadius: "8px",
        _focus: {
          borderColor: "brand.primary",
        },
        _hover: {
          background: "whiteAlpha.200",
        },
        _invalid: {
          borderColor: "red.500",
        },
      },
      "filled-light": {
        color: "gray.900",
        background: "gray.100",
        border: "2px solid",
        borderColor: "transparent",
        borderRadius: "8px",
        _focus: {
          borderColor: "brand.primary",
        },
        _hover: {
          background: "blackAlpha.200",
        },
        _invalid: {
          borderColor: "red.500",
        },
      },
    },
    defaultProps: {
      variant: "filled",
    },
  },
  Switch: {
    defaultProps: {
      colorScheme: "primary",
    },
  },
  FormError: {
    baseStyle: {
      text: {
        color: "brand.negative",
      },
    },
  },
  FormLabel: {
    baseStyle: {
      color: "gray.100",
      textTransform: "uppercase",
      fontSize: "0.75rem",
      fontWeight: "normal",
    },
  },
  Progress: {
    parts: ["track", "filledTrack"],
    baseStyle: {
      track: {
        borderRadius: "100px",
      },
      filledTrack: {
        background: "gray.100",
      },
    },
    sizes: {},
    defaultProps: {
      colorScheme: "gray",
      size: "sm",
    },
  },
  Breadcrumb: {
    parts: ["item", "link","container"],
    baseStyle: {
      link: {
        color: "primary.200",
        _hover: {
          color: "primary.300",
        },
      },
      item: {
        color: "gray.300",
        '[aria-current="page"]': {
          color: "inherit"
        },
        '[role="presentation"]': {
          marginInlineEnd: '0.25rem',
          marginInlineStart: '0.25rem',
        },
        container: {
          marginLeft: "2.5rem",
        },
      },
    },
  },
  Slider: {
    parts: ["track", "thumb", "filledTrack", "mark"],
    variants: {
      solid: {
        track: {
          background: "brand.darker",
          height: "8px",
          borderRadius: "4px",
          _disabled: {
            background: "brand.darker",
          },
        },
        filledTrack: {
          bg: "primary.200",
          _disabled: {
            background: "gray.1000",
          },
        },
        thumb: {
          background: "gray.100",
          width: "16px",
          height: "16px",
          zIndex: 0,
          _disabled: {
            background: "gray.300",
          },
        },
      },
    },
  },
  RangeSlider: {
    parts: ["track", "thumb", "filledTrack", "mark"],
    variants: {
      solid: {
        track: {
          background: "brand.darker",
          height: "8px",
          borderRadius: "4px",
          _disabled: {
            background: "brand.darker",
          },
        },
        filledTrack: {
          bg: "primary.400",
          _disabled: {
            background: "gray.1000",
          },
        },
        thumb: {
          background: "gray.100",
          width: "16px",
          height: "16px",
          zIndex: 0,
          _disabled: {
            background: "gray.300",
          },
        },
      },
    },
  },
};

const styles = {
  global: {
    // reset default global styles - https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/styles.ts
    body: null,
    "*::placeholder": null,
    // '*, *::before, &::after': null,
    // add reset css by class names - https://github.com/chakra-ui/chakra-ui/blob/main/packages/css-reset/src/css-reset.tsx
    // add others elements if needed
    ".chakra-button, .chakra-button::before, .chakra-button::after, .chakra-input, .chakra-editable__input":
      {
        borderWidth: "0",
        borderStyle: "solid",
        boxSizing: "border-box",
      },
    ".chakra-button, .chakra-input, .chakra-editable__input, .chakra-optgroup, .chakra-select, .chakra-textarea, .chakra-switch":
      {
        fontFamily: "inherit",
        fontSize: "100%",
        margin: "0",
        padding: "0",
        lineHeight: "inherit",
        color: "inherit",
      },
    ".chakra-button, .chakra-input, .chakra-editable__input": {
      overflow: "visible",
    },
    '.chakra-button::-moz-focus-inner, .chakra-input[type="button"]::-moz-focus-inner, .chakra-input[type="reset"]::-moz-focus-inner, .chakra-input[type="submit"]::-moz-focus-inner':
      {
        borderStyle: "none",
        padding: "0",
      },
    '.chakra-input[type="number"]': {
      "-webkit-appearance": "textfield",
    },
    ".chakra-textarea": {
      overflow: "auto",
      resize: "vertical",
    },
    '.chakra-input[type="checkbox"], .chakra-input[type="radio"]': {
      boxSizing: "border-box",
      padding: "0",
    },
    '.chakra-input[type="number"]::-webkit-inner-spin-button, .chakra-input[type="number"]::-webkit-outer-spin-button':
      {
        "-webkit-appearance": "textfield",
      },
    '.chakra-input[type="search"]': {
      "-webkit-appearance": "textfield",
      outlineOffset: "-2px",
    },
    '.chakra-input[type="search"]::-webkit-search-decoratio': {
      "-webkit-appearance": "none !important",
    },
    ".chakra-input::-webkit-file-upload-button": {
      "-webkit-appearance": "button",
      font: "inherit",
    },
    ".chakra-fieldset": {
      margin: "0",
      padding: "0",
    },
    ".chakra-button": {
      background: "transparent",
      padding: "0",
    },
    ".chakra-button::-moz-focus-inner": {
      border: "0 !important",
    },
    ".chakra-select::-ms-expand": {
      display: "none",
    },
    "[data-js-focus-visible] :focus:not([data-focus-visible-added])": {
      outline: "none",
      boxShadow: "none",
    },
    '.chakra-breadcrumb__link[aria-current="page"]': {
      color: "white",
      ":hover": {
        color: "white",
        cursor: "default",
        textDecoration: "none",
      },
    },
    "::-webkit-scrollbar": {
        width: "8px",
        height: "3px"
    },
    "::-webkit-scrollbar-track": {
      backgroundColor: "#646464"
    },
    "::-webkit-scrollbar-track-piece": {
      backgroundColor: "#000"
    },
    "::-webkit-scrollbar-thumb": {
      height: "50px",
      backgroundColor: "#666",
      borderRadius: "3px"
    },
    "::-webkit-scrollbar-corner": {
      backgroundColor: "#646464"
    },
    "::-webkit-resizer": {
      backgroundColor: "#666"
    }
  },
};

export const ALERT_STYLES = {
  info: { icon: FaInfo, background: "brand.safe_dark", color: "white" },
  warning: {
    icon: FaExclamationTriangle,
    background: "brand.warning",
    color: "white",
  },
  success: {
    icon: FaRegSmile,
    background: "brand.positive_light",
    color: "gray.600",
  },
  error: { icon: FaRegSurprise, background: "brand.negative", color: "white" },
};

export const TOAST_STYLES = {
  info: {
    icon: FaInfo,
    background: "brand.grey-darker",
    color: "gray.100",
    iconColor: "brand.safe_dark",
  },
  warning: {
    icon: FaExclamationTriangle,
    background: "brand.grey-darker",
    color: "gray.100",
    iconColor: "brand.warning",
  },
  success: {
    icon: FaRegSmile,
    background: "brand.grey-darker",
    color: "gray.100",
    iconColor: "brand.positive_light",
  },
  error: {
    icon: FaRegSurprise,
    background: "brand.grey-darker",
    color: "gray.100",
    iconColor: "brand.negative",
  },
};

const config = {
  initialColorMode: "dark",
  useSystemColorMode: false,
};

const fonts = {
  body: 'Rubik, sans-serif',
  heading: 'Rubik, sans-serif',
}

export const theme = extendTheme({
  fonts,
  colors,
  breakpoints,
  components,
  styles,
  config,
  shadows,
});
